// Paging rules shared by the infinite-scroll list hooks (useControlsV2,
// useWorkSteps).
//
// - `reset`: start over at the first page (filters changed, or a refetch).
// - `more`: append the next page (infinite scroll).
// - `refresh`: reload the first page in place, keeping later pages already
//   loaded — newly created rows appear without the list snapping back to
//   one page. Rows the first page now includes are dropped from the tail.
export type ListFetchMode = "reset" | "more" | "refresh";

export const mergeListPage = <TRow extends { id: string }>(
  mode: ListFetchMode,
  previous: TRow[],
  page: TRow[],
): TRow[] => {
  if (mode === "reset") return page;
  if (mode === "more") {
    return [
      ...previous,
      ...page.filter((row) => !previous.some((known) => known.id === row.id)),
    ];
  }
  const pageIds = new Set(page.map((row) => row.id));
  return [...page, ...previous.filter((row) => !pageIds.has(row.id))];
};

// The offset the next `more` fetch starts from. A refresh leaves it where
// the loaded pages end; later pages shifted by newly created rows come back
// as duplicates, which `more` drops.
export const nextListOffset = (
  mode: ListFetchMode,
  loadedOffset: number,
  requestOffset: number,
  pageSize: number,
): number => {
  if (mode === "reset") return pageSize;
  if (mode === "more") return requestOffset + pageSize;
  return Math.max(loadedOffset, pageSize);
};
